import { useMemo } from 'react';
import {
  can as sharedCan,
  canDo,
  canAccessProject as sharedCanAccessProject,
  type AccessContext,
  type Capability,
  type OrgSummary,
} from '@aiai/shared';
import { useOrg } from '../org/useOrg';
import { useMyGrants } from './useMyGrants';

/**
 * Workspace capabilities for the current user, reading the SAME shared policy
 * the API enforces so the UI never shows a control the server would 403.
 *
 * Three shapes, because the app has two kinds of page plus a resource layer:
 *  - `can(cap, resource?)` — for workspace-scoped pages (`/w/:slug/…`), where
 *    the active workspace is the one in the URL. Resolves the active
 *    workspace's org role AND, when a `projectId` resource is passed, the
 *    caller's per-project grant in that project (via `canDo`/`AccessContext`)
 *    — so a project `contributor` gets issue-board capabilities there even
 *    though the org `member` role alone holds nothing. `resource` is
 *    optional so existing `can(cap)` call sites keep compiling.
 *  - `canAccessProject(projectId)` — may this user see/enter this project at
 *    all (owner, or holds any grant on it)?
 *  - `canInAny(cap)` / `workspacesWith(cap)` — for the global entry pages
 *    (Projects/Prototypes/Overview), which span every workspace: a create
 *    action is offered if the user may do it in AT LEAST ONE workspace, and
 *    the workspace picker then lists exactly those. These stay org-role
 *    based (they gate owner-only workspace management, which grants never
 *    extend) and are unaffected by per-project grants.
 *
 * There is no platform role here — matching the API, every user is bound by
 * their active workspace role + grants (platform operators live on the
 * separate ops plane).
 */
export function useCapabilities(): {
  /** Owner of the active workspace — gates owner-only knobs that aren't a
   *  capability, e.g. raising the per-run cost cap on Run again. */
  isWorkspaceOwner: boolean;
  can: (capability: Capability, resource?: { projectId?: string }) => boolean;
  canAccessProject: (projectId: string) => boolean;
  canInAny: (capability: Capability) => boolean;
  workspacesWith: (capability: Capability) => OrgSummary[];
} {
  const { activeOrg, orgs } = useOrg();
  const grants = useMyGrants();

  return useMemo(() => {
    const ctx: AccessContext = {
      orgRole: activeOrg?.role ?? 'member',
      grants,
    };
    const can = (capability: Capability, resource?: { projectId?: string }) =>
      canDo(ctx, capability, resource);
    const canAccessProject = (projectId: string) =>
      sharedCanAccessProject(ctx, projectId);
    // Offered a workspace only where the user's real role holds the capability.
    const workspacesWith = (capability: Capability) =>
      orgs.filter((o) => sharedCan(o.role, capability));
    const canInAny = (capability: Capability) =>
      orgs.some((o) => sharedCan(o.role, capability));
    const isWorkspaceOwner = ctx.orgRole === 'owner';
    return { isWorkspaceOwner, can, canAccessProject, canInAny, workspacesWith };
  }, [activeOrg, orgs, grants]);
}
